import { cx, PassProps } from './hooks';
import { Icon, IconName } from './Icon';
import { Button } from './Button';
import { t } from '../i18n';

/* ---------------- Skeleton ---------------- */
export interface SkeletonProps extends PassProps {
  variant?: 'text' | 'card' | 'avatar' | 'row';
  /** text: number of lines */
  lines?: number;
  size?: number;
  className?: string;
}
export function Skeleton({ variant = 'text', lines = 3, size = 46, className, ...rest }: SkeletonProps) {
  if (variant === 'avatar') return <span className={cx('pl-skeleton pl-skeleton--avatar', className)} style={{ inlineSize: size, blockSize: size }} aria-hidden="true" {...rest} />;
  if (variant === 'card') return <div className={cx('pl-skeleton pl-skeleton--card', className)} aria-hidden="true" {...rest} />;
  if (variant === 'row') {
    return (
      <div className={cx('pl-skeleton-row', className)} aria-hidden="true" {...rest}>
        <span className="pl-skeleton pl-skeleton--avatar" style={{ inlineSize: size, blockSize: size }} />
        <span className="pl-skeleton-row__lines"><span className="pl-skeleton pl-skeleton--line" style={{ inlineSize: '55%' }} /><span className="pl-skeleton pl-skeleton--line" style={{ inlineSize: '35%' }} /></span>
      </div>
    );
  }
  const widths = ['100%', '86%', '62%', '92%', '48%'];
  return (
    <div className={cx('pl-skeleton-text', className)} aria-hidden="true" {...rest}>
      {Array.from({ length: lines }, (_, i) => <span key={i} className="pl-skeleton pl-skeleton--line" style={{ inlineSize: widths[i % widths.length] }} />)}
    </div>
  );
}

/** Wrap skeleton groups so assistive tech hears one "loading" instead of nothing. */
export function Loading({ label = t('common.a11y.loading'), children, ...rest }: { label?: string; children?: any } & PassProps) {
  return <div role="status" aria-busy="true" {...rest}><span className="pl-sr-only">{label}</span>{children}</div>;
}

/* ---------------- EmptyState ---------------- */
export interface EmptyStateProps extends PassProps {
  /** Custom illustration (SVG/img). Defaults to a soft badge with `icon`. */
  art?: any;
  icon?: IconName;
  title: string;
  body?: string;
  /** Primary action node, usually a <Button>. */
  action?: any;
  className?: string;
}
export function EmptyState({ art, icon = 'calendar', title, body, action, className, ...rest }: EmptyStateProps) {
  return (
    <div className={cx('pl-empty', className)} {...rest}>
      <div className="pl-empty__art" aria-hidden="true">
        {art ?? (
          <span className="pl-badge-art"><i className="pl-badge-art__dot pl-badge-art__dot--a" /><i className="pl-badge-art__dot pl-badge-art__dot--b" /><span className="pl-badge-art__core"><Icon name={icon} size={34} /></span></span>
        )}
      </div>
      <h2 className="pl-h3">{title}</h2>
      {body ? <p className="pl-muted pl-balance">{body}</p> : null}
      {action ? <div className="pl-empty__action">{action}</div> : null}
    </div>
  );
}

/* ---------------- ErrorState ---------------- */
export interface ErrorStateProps extends PassProps {
  /** data-testid for the retry button. */
  retryTestid?: string;
  title?: string;
  body?: string;
  retryLabel?: string;
  onRetry?: () => void;
  /** Extra action (e.g. back home link). */
  secondary?: any;
  className?: string;
}
export function ErrorState({ title = t('common.ui.errTitle'), body = t('common.ui.errBody'), retryLabel = t('common.btn.retry'), onRetry, secondary, className, retryTestid, ...rest }: ErrorStateProps) {
  return (
    <div className={cx('pl-empty pl-empty--error', className)} role="alert" {...rest}>
      <div className="pl-empty__art" aria-hidden="true"><span className="pl-badge-art pl-badge-art--error"><span className="pl-badge-art__core"><Icon name="alert" size={34} /></span></span></div>
      <h2 className="pl-h3">{title}</h2>
      <p className="pl-muted pl-balance">{body}</p>
      <div className="pl-empty__action">
        {onRetry ? <Button variant="primary" size="lg" onClick={onRetry} data-testid={retryTestid}>{retryLabel}</Button> : null}
        {secondary}
      </div>
    </div>
  );
}

/* ---------------- Banner ---------------- */
export interface BannerProps extends PassProps {
  tone?: 'demo' | 'info' | 'success' | 'warning' | 'danger';
  icon?: IconName;
  children?: any;
  /** Optional trailing action, e.g. <button>איפוס</button>. */
  action?: any;
  /** Stick to the top of the column (demo banner). */
  sticky?: boolean;
  className?: string;
}
const BANNER_ICON: Record<string, IconName> = { demo: 'info', info: 'info', success: 'check-circle', warning: 'alert', danger: 'alert' };
export function Banner({ tone = 'info', icon, children, action, sticky, className, ...rest }: BannerProps) {
  return (
    <div className={cx('pl-banner', `pl-banner--${tone}`, sticky && 'pl-banner--sticky', className)} role={tone === 'danger' ? 'alert' : 'status'} {...rest}>
      <Icon name={icon ?? BANNER_ICON[tone] ?? 'info'} size={20} />
      <span className="pl-banner__text">{children}</span>
      {action ? <span className="pl-banner__action">{action}</span> : null}
    </div>
  );
}

/* ---------------- Celebrate (confetti-free) ---------------- */
export function Celebrate({ size = 96, label, className, ...rest }: { size?: number; label?: string; className?: string } & PassProps) {
  return (
    <span className={cx('pl-celebrate', className)} style={{ inlineSize: size, blockSize: size }} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} {...rest}>
      <span className="pl-celebrate__ring" />
      <span className="pl-celebrate__disc">
        <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" focusable="false">
          <path className="pl-celebrate__check" d="M5 12.5l4.5 4.5L19 7.5" pathLength="30" />
        </svg>
      </span>
    </span>
  );
}

/* ---------------- SuccessHero (brand celebration moment) ---------------- */
const CONFETTI = 10;
/** Booking confirmed / plan upgraded / first booking. Check disc + a short confetti burst (240 ms). Reduced motion: static, no confetti. */
export function SuccessHero({ title, body, size = 88, children, className, ...rest }: { title: string; body?: string; size?: number; children?: any; className?: string } & PassProps) {
  return (
    <section className={cx('pl-successhero', className)} {...rest}>
      <span className="pl-successhero__art" aria-hidden="true">
        {Array.from({ length: CONFETTI }, (_, i) => <i key={i} className="pl-successhero__bit" style={{ ['--i' as any]: i }} />)}
        <Celebrate size={size} />
      </span>
      <h1 className="pl-successhero__title">{title}</h1>
      {body ? <p className="pl-successhero__body">{body}</p> : null}
      {children ? <div className="pl-successhero__actions">{children}</div> : null}
    </section>
  );
}

/* ---------------- Progress ---------------- */
export function ProgressDots({ count, index, label, ...rest }: { count: number; index: number; label?: string } & PassProps) {
  return (
    <div className="pl-dots" role="img" aria-label={label ?? t('common.ui.stepOf', { index: index + 1, count })} {...rest}>
      {Array.from({ length: count }, (_, i) => <span key={i} className={cx('pl-dots__dot', i === index && 'is-active', i < index && 'is-done')} />)}
    </div>
  );
}

export function ProgressRing({ value, size = 64, stroke = 6, children, label, ...rest }: { value: number; size?: number; stroke?: number; children?: any; label?: string } & PassProps) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.min(1, Math.max(0, value));
  return (
    <span className="pl-ring" style={{ inlineSize: size, blockSize: size }} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v * 100)} aria-label={label} {...rest}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle className="pl-ring__track" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} />
        <circle className="pl-ring__bar" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - v)} />
      </svg>
      {children ? <span className="pl-ring__center">{children}</span> : null}
    </span>
  );
}

/** Numbered steps; runs from the inline start (the reading side). */
export function Stepper({ steps, current, ...rest }: { steps: string[]; current: number } & PassProps) {
  return (
    <ol className="pl-stepper" aria-label={t('common.ui.progress')} {...rest}>
      {steps.map((s, i) => (
        <li key={s} className={cx('pl-stepper__item', i < current && 'is-done', i === current && 'is-current')} aria-current={i === current ? 'step' : undefined}>
          <span className="pl-stepper__dot">{i < current ? <Icon name="check" size={14} stroke={3} /> : i + 1}</span>
          <span className="pl-stepper__label">{s}</span>
        </li>
      ))}
    </ol>
  );
}
